import {useEffect, useState} from "react";
import * as localforage from "localforage";
import {EBirdChecklistByFamily, EBirdMyDataSchema} from "ebird-mydata-reader";
import {getFamilySlug, parseObservationDateTime} from "./helpers";
import {DEBUG_PERF, endPerfTimer, startPerfTimer} from "./perf";
import {
    DASHBOARD_SUMMARY_CACHE_KEY,
    DASHBOARD_SUMMARY_CACHE_VERSION,
    DashboardRecentActivity,
    DashboardSummary,
    DashboardYearStats
} from "./birdDataCache";

const getNumYears = (years: Array<number|string>) => {
    if (!years.length) return 0;

    const firstYear = parseInt(years[0] as string);
    const lastYear = parseInt(years[years.length - 1] as string);
    if (Number.isNaN(firstYear) || Number.isNaN(lastYear)) return years.length;

    return Math.max(firstYear - lastYear, 1);
}

const buildSpeciesDetailPaths = (checklist: EBirdChecklistByFamily[]) => {
    const speciesDetailPaths: Record<string, string> = {};

    for (const family of checklist) {
        const familySlug = getFamilySlug(family.familyName);

        for (const species of family.speciesList) {
            const detailPath = `/family-explorer/family/${familySlug}/species/${species.speciesCode}`;
            speciesDetailPaths[species.taxonomicOrder] = detailPath;

            for (const alternateTaxonomicOrder of species.alternateTaxonomicOrders ?? []) {
                speciesDetailPaths[alternateTaxonomicOrder] = detailPath;
            }
        }
    }

    return speciesDetailPaths;
}

const buildYearStats = (rawData: EBirdMyDataSchema[]) => {
    const yearBuckets = new Map<number, {
        species: Set<string|number>,
        locations: Set<string>,
        days: Set<string>,
        lifers: Set<string|number>
    }>();

    for (const observation of rawData) {
        const observedYear = observation.observedYear;
        if (!observedYear) continue;

        if (!yearBuckets.has(observedYear)) {
            yearBuckets.set(observedYear, {
                species: new Set<string|number>(),
                locations: new Set<string>(),
                days: new Set<string>(),
                lifers: new Set<string|number>()
            });
        }

        const bucket = yearBuckets.get(observedYear);
        if (!bucket) continue;

        const speciesKey = observation.taxonomicOrder ?? observation.commonName;
        const locationKey = observation.locationId ?? observation.location;

        if (speciesKey) bucket.species.add(speciesKey);
        if (locationKey) bucket.locations.add(locationKey);
        if (observation.date) bucket.days.add(observation.date);
        if (observation.isLifer && speciesKey) bucket.lifers.add(speciesKey);
    }

    const yearStatsByYear: Record<string, DashboardYearStats> = {};
    for (const [year, bucket] of Array.from(yearBuckets.entries())) {
        yearStatsByYear[year] = {
            species: bucket.species.size,
            locations: bucket.locations.size,
            days: bucket.days.size,
            lifers: bucket.lifers.size
        };
    }

    return yearStatsByYear;
}

const buildRecentActivity = (
    rawData: EBirdMyDataSchema[],
    speciesDetailPathByTaxonomicOrder: Record<string, string>
) => {
    const latestBySpecies = new Map<string, DashboardRecentActivity>();

    for (const observation of rawData) {
        if (!observation.date) continue;

        const key = observation.taxonomicOrder?.toString() || observation.commonName;
        if (!key) continue;

        const observationDate = parseObservationDateTime(observation.date, observation.time);
        const observationTimestamp = observationDate?.toMillis() ?? 0;
        const existingObservation = latestBySpecies.get(key);
        const existingDate = existingObservation?.timestamp ?? 0;

        if (!existingObservation || observationTimestamp > existingDate) {
            const locationBits = [observation.location, observation.county, observation.stateProvince].filter(Boolean);

            latestBySpecies.set(key, {
                id: key,
                title: observation.commonName,
                meta: locationBits.join(' - '),
                dateLabel: observationDate?.isValid
                    ? observationDate.toLocaleString({month: 'short', day: 'numeric', year: 'numeric'})
                    : observation.date ?? '',
                taxonomicOrder: observation.taxonomicOrder,
                speciesDetailPath: observation.taxonomicOrder
                    ? speciesDetailPathByTaxonomicOrder[observation.taxonomicOrder]
                    : undefined,
                timestamp: observationTimestamp
            });
        }
    }

    return Array.from(latestBySpecies.values())
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, 4);
}

const buildDashboardSummary = (
    rawData: EBirdMyDataSchema[],
    checklist: EBirdChecklistByFamily[],
    years: Array<number|string>
): DashboardSummary => {
    const totalTimer = startPerfTimer('dashboard:buildSummary');
    const pathTimer = startPerfTimer('dashboard:buildSpeciesDetailPaths');
    const speciesDetailPathByTaxonomicOrder = buildSpeciesDetailPaths(checklist);
    endPerfTimer(pathTimer);
    const yearStatsTimer = startPerfTimer('dashboard:buildYearStats');
    const yearStatsByYear = buildYearStats(rawData);
    endPerfTimer(yearStatsTimer);
    const recentActivityTimer = startPerfTimer('dashboard:buildRecentActivity');
    const recentActivity = buildRecentActivity(rawData, speciesDetailPathByTaxonomicOrder);
    endPerfTimer(recentActivityTimer);

    const summary = {
        version: DASHBOARD_SUMMARY_CACHE_VERSION,
        speciesDetailPathByTaxonomicOrder,
        yearStatsByYear,
        recentActivity,
        numYears: getNumYears(years)
    };
    endPerfTimer(totalTimer);

    return summary;
}

export function useDashboardSummary(
    rawData: EBirdMyDataSchema[],
    checklist: EBirdChecklistByFamily[],
    years: Array<number|string>
) {
    const [dashboardSummary, setDashboardSummary] = useState<DashboardSummary>();

    useEffect(() => {
        let isActive = true;

        const resolveDashboardSummary = async () => {
            if (!rawData.length || !checklist.length) {
                setDashboardSummary(undefined);
                return;
            }

            const totalTimer = startPerfTimer('dashboard:resolveSummary');
            try {
                const cacheTimer = startPerfTimer('dashboard:cacheLookup');
                const cachedSummary: DashboardSummary | null = await localforage.getItem(DASHBOARD_SUMMARY_CACHE_KEY);
                endPerfTimer(cacheTimer);

                if (
                    cachedSummary?.version === DASHBOARD_SUMMARY_CACHE_VERSION
                    && cachedSummary.recentActivity?.length
                    && Object.keys(cachedSummary.yearStatsByYear ?? {}).length
                ) {
                    if (DEBUG_PERF) console.debug('dashboard:cacheHit', {
                        recentActivityCount: cachedSummary.recentActivity.length,
                        yearCount: Object.keys(cachedSummary.yearStatsByYear ?? {}).length
                    });
                    if (!isActive) return;

                    setDashboardSummary(cachedSummary);
                    return;
                }

                if (DEBUG_PERF) console.debug('dashboard:cacheMiss');
                const nextSummary = buildDashboardSummary(rawData, checklist, years);
                const cacheWriteTimer = startPerfTimer('dashboard:cacheWrite');
                await localforage.setItem(DASHBOARD_SUMMARY_CACHE_KEY, nextSummary);
                endPerfTimer(cacheWriteTimer);

                if (!isActive) return;
                setDashboardSummary(nextSummary);
            } catch (error) {
                console.error(error);

                if (DEBUG_PERF) console.debug('dashboard:fallbackRebuild');
                const nextSummary = buildDashboardSummary(rawData, checklist, years);
                if (!isActive) return;
                setDashboardSummary(nextSummary);
            } finally {
                endPerfTimer(totalTimer);
            }
        }

        void resolveDashboardSummary();

        return () => {
            isActive = false;
        }
    }, [checklist, rawData, years]);

    return dashboardSummary;
}
